import { useEffect, useMemo, useRef, useState } from 'react'

import { useAccountReadContext } from '@app/context/AccountContextContext'
import { useSessionEntity } from '@app/context/EntityContext'
import { useProductReadContext } from '@app/context/ProductContext'

import { useIBRoutesConditions } from '@pages/IntroducingBroker/IntroducingBrokerRoutes'
import { useTradeRoutesConditions } from '@pages/Trade/Trade.routes'

import { TourDefinition } from '@ui/components/Tourtip/Tourtip'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { TickmillCompaniesEnum } from '@utils/companyName.utils'
import { useWindowResize } from '@utils/domUtils'
import { isZero } from '@utils/validations'

import { TickmillProductType } from '@model/TickmillProductType'
import { WalletTypeEnum } from '@model/WalletDto'

export const useWalletsTour = (enabled = true): TourDefinition | null => {
  const [isZeroAvailableWalletTypes, setIsZeroAvailableWalletTypes] = useState<boolean | null>(null)
  const apiClient = useApiClient(ClientApiClient)
  const { isDefaultCFDProductType } = useProductReadContext()
  const hasFetchedRef = useRef(false)
  const walletType = isDefaultCFDProductType() ? WalletTypeEnum.CFD : WalletTypeEnum.ETD
  const { canAccessTrade } = useTradeRoutesConditions()
  const { account } = useAccountReadContext()
  const { isIBVisible } = useIBRoutesConditions()
  const isMobile = useWindowResize()
  const entity = useSessionEntity()

  const hasProductSwitcher = useMemo(() => {
    return (
      entity === TickmillCompaniesEnum.TICKMILL_UK &&
      !!account?.visibilityConfiguration?.showProductSwitcher
    )
  }, [entity, account?.visibilityConfiguration?.showProductSwitcher])

  useEffect(() => {
    if (!enabled) {
      setIsZeroAvailableWalletTypes(null)
      hasFetchedRef.current = false
      return
    }

    if (hasFetchedRef.current) {
      return
    }

    const loadTour = async () => {
      try {
        const availableWalletTypes = await apiClient.getAvailableWallets(walletType)
        setIsZeroAvailableWalletTypes(isZero(availableWalletTypes.length))
        hasFetchedRef.current = true
      } catch (error) {
        console.error('Failed to fetch available wallets:', error)
        hasFetchedRef.current = true
      }
    }

    loadTour()
  }, [enabled])

  const tour = useMemo(() => {
    if (!enabled) {
      return null
    }

    return {
      id: 'wallets-onboarding',
      version: 1,
      products: [TickmillProductType.CFD],
      steps: [
        {
          id: 'intro',
          kind: 'intro',
          target: { kind: 'virtual' },
          preferredPlacement: 'auto',
          header: 'tour.wallets.intro.header',
          description: 'tour.wallets.intro.description',
        },
        {
          id: 'wallet',
          kind: 'regular',
          target: { kind: 'data-attr', key: 'wallets.wallet' },
          preferredPlacement: { mobile: 'top-center', desktop: 'top-left' },
          header: 'tour.wallets.wallet.header',
          description: 'tour.wallets.wallet.description',
        },
        {
          id: 'deposit',
          kind: 'regular',
          target: {
            kind: 'data-attr',
            key: isMobile ? 'wallets.deposit' : 'wallets.actions',
          },
          preferredPlacement: {
            desktop: 'bottom-center',
            mobile: 'bottom-center',
          },
          header: 'tour.wallets.deposit.header',
          description: 'tour.wallets.deposit.description',
        },
        {
          id: 'transfer',
          kind: 'regular',
          target: {
            kind: 'data-attr',
            key: isMobile ? 'wallets.transfer' : 'wallets.actions',
          },
          preferredPlacement: {
            desktop: 'bottom-left',
            mobile: 'bottom-center',
          },
          header: 'tour.wallets.transfer.header',
          description: 'tour.wallets.transfer.description',
        },
        {
          id: 'withdrawal',
          kind: 'regular',
          target: {
            kind: 'data-attr',
            key: isMobile ? 'wallets.withdrawal' : 'wallets.actions',
          },
          preferredPlacement: {
            desktop: 'bottom-left',
            mobile: 'bottom-center',
          },
          header: 'tour.wallets.withdrawal.header',
          description: 'tour.wallets.withdrawal.description',
        },
        {
          id: 'add-wallet',
          kind: 'regular',
          target: { kind: 'data-attr', key: 'wallets.add' },
          preferredPlacement: {
            desktop: 'top-right',
            mobile: 'top-right',
          },
          header: 'tour.wallets.addWallet.header',
          description: 'tour.wallets.addWallet.description',
          descriptionMobile: 'tour.wallets.addWallet.descriptionMobile',
          condition: !isZeroAvailableWalletTypes,
        },
        {
          id: 'nav',
          kind: 'regular',
          target: { kind: 'data-attr', key: 'wallets.nav' },
          preferredPlacement: {
            desktop: 'left-center',
            mobile: 'top-center',
          },
          header: 'tour.wallets.navigation.header',
          description: 'tour.wallets.navigation.description',
          descriptionMobile: 'tour.wallets.navigation.descriptionMobile',
        },
        {
          id: 'trade',
          kind: 'regular',
          target: { kind: 'data-attr', key: 'navigation.trade' },
          preferredPlacement: {
            desktop: 'left-center',
            mobile: 'top-center',
          },
          header: 'tour.wallets.trade.header',
          description: 'tour.wallets.trade.description',
          condition: canAccessTrade,
        },
        {
          id: 'expandAndCollapse',
          kind: 'regular',
          target: { kind: 'data-attr', key: 'wallets.expandAndCollapse' },
          preferredPlacement: {
            desktop: 'left-center',
          },
          header: 'tour.wallets.expandAndCollapse.header',
          description: 'tour.wallets.expandAndCollapse.description',
          condition: !isMobile,
        },
        {
          id: 'accountSwitch',
          kind: 'regular',
          target: { kind: 'data-attr', key: 'wallets.accountSwitch' },
          preferredPlacement: {
            desktop: 'left-center',
          },
          header: 'tour.wallets.accountSwitch.header',
          description: 'tour.wallets.accountSwitch.description',
          condition: !isMobile && isIBVisible,
        },
        {
          id: 'productSwitch',
          kind: 'regular',
          target: { kind: 'data-attr', key: 'wallets.productSwitch' },
          preferredPlacement: {
            desktop: 'left-bottom',
          },
          header: 'tour.wallets.productSwitch.header',
          description: 'tour.wallets.productSwitch.description',
          condition: !isMobile && hasProductSwitcher,
        },
        {
          id: 'profile',
          kind: 'regular',
          target: { kind: 'data-attr', key: 'wallets.profile' },
          preferredPlacement: {
            desktop: 'top-right',
          },
          header: 'tour.wallets.profile.header',
          description: 'tour.wallets.profile.description',
          condition: !isMobile,
        },

        {
          id: 'outro',
          kind: 'outro',
          target: { kind: 'virtual' },
          preferredPlacement: 'auto',
          header: 'tour.wallets.outro.header',
          description: 'tour.wallets.outro.description',
          primaryActionText: 'tour.wallets.outro.primaryActionText',
          primaryActionCommand: 'wallets.outro.deposit',
        },
      ],
    } as TourDefinition
  }, [
    canAccessTrade,
    enabled,
    isZeroAvailableWalletTypes,
    isMobile,
    isIBVisible,
    hasProductSwitcher,
  ])

  return tour
}
